import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Link,
  Svg,
  Path,
  G,
} from "@react-pdf/renderer";
import { convertEpochToDateFormat } from "../../Utils/convertEpochToDateFormat";
import { formatEmailToName } from "../../Utils/formatEmailToName";
import { getPastReferencesList, joinReferenceIds } from "../../Utils/recurrenceReferences";

// ── Helpers ────────────────────────────────────────────────

const formatUtcDate = (raw) => {
  if (!raw) return "NA";
  const normalised = raw.replace(" ", "T").replace(/(\.\d+)?$/, (m) =>
    /Z|[+-]\d{2}:\d{2}$/.test(raw) ? m : `${m}Z`
  );
  const d = new Date(normalised);
  if (isNaN(d)) return "NA";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC";
};

// Strip HTML tags, convert <br> to newline
const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .trim();
};

const getPriorityLabel = (p) => {
  if (p == null) return "NA";
  return `P${p}`;
};

// ── Color palette ──────────────────────────────────────────
const C = {
  navy:     "#00243D",
  blue:     "#42B0D5",
  white:    "#FFFFFF",
  dark:     "#141414",
  muted:    "#6B6B6B",
  border:   "#E0E0E0",
  bg:       "#F7F9FB",
  rowAlt:   "#F7F9FB",
  p0:       "#7B1FA2",
  p1:       "#C0392B",
  p2:       "#E67E22",
  p3:       "#F59E0B",
  p4:       "#42B0D5",
  success:  "#1A7F4B",
  successBg:"#EAF7EF",
  errorBg:  "#FCDDE0",
  error:    "#C0392B",
  warnBg:   "#FFF3CD",
  warn:     "#856404",
  neutralBg:"#F7F7F7",
  neutral:  "#616161",
};

const priorityColor = (p) => {
  const map = { "0": C.p0, "1": C.p1, "2": C.p2, "3": C.p3, "4": C.p4 };
  return map[String(p)] || C.muted;
};

const getStatusColors = (status, duedate) => {
  if (status?.toLowerCase() === "done") return { bg: C.successBg, text: C.success };
  if (duedate) {
    const due = new Date(duedate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due < today) return { bg: C.errorBg, text: C.error };
    return { bg: C.warnBg, text: C.warn };
  }
  return { bg: C.neutralBg, text: C.neutral };
};

// ── Styles ─────────────────────────────────────────────────
const S = StyleSheet.create({
  page: {
    backgroundColor: "#FFFFFF",
    paddingTop: 56,
    paddingBottom: 36,
    paddingHorizontal: 28,
  },
  // Fixed page header
  fixedHeader: {
    position: "absolute",
    top: 0, left: 0, right: 0,
    height: 44,
    backgroundColor: C.blue,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
  },
  fixedHeaderRight: {
    fontSize: 7,
    color: "rgba(255,255,255,0.85)",
    fontFamily: "Helvetica",
    letterSpacing: 0.8,
  },
  // Fixed footer
  fixedFooter: {
    position: "absolute",
    bottom: 0, left: 0, right: 0,
    height: 28,
    backgroundColor: C.blue,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
  },
  fixedFooterText: {
    fontSize: 7,
    color: "rgba(255,255,255,0.85)",
    fontFamily: "Helvetica",
  },
  // Section pattern (matches sre-section-card)
  sectionCard: {
    marginTop: 14,
    borderWidth: 1,
    borderColor: C.border,
  },
  sectionHeader: {
    backgroundColor: C.blue,
    paddingVertical: 9,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionHeaderText: {
    color: "#FFFFFF",
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.8,
  },
  sectionBody: {
    paddingVertical: 14,
    paddingHorizontal: 14,
  },
  // Body text
  bodyText: {
    fontSize: 9,
    color: C.dark,
    lineHeight: 1.6,
    fontFamily: "Helvetica",
  },
  naText: {
    fontSize: 9,
    color: C.muted,
    fontFamily: "Helvetica",
    fontStyle: "italic",
  },
  // Metadata grid (report-meta-grid)
  metaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  metaField: {
    width: "25%",
    marginBottom: 12,
    paddingRight: 8,
  },
  metaLabel: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: C.muted,
    marginBottom: 3,
  },
  metaValue: {
    fontSize: 9,
    color: C.dark,
    fontFamily: "Helvetica",
  },
  metaLink: {
    fontSize: 9,
    color: C.dark,
    fontFamily: "Helvetica-Bold",
    textDecoration: "underline",
  },
  subsectionTitle: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: C.muted,
    letterSpacing: 0.6,
    marginBottom: 8,
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: C.border,
  },
  divider: {
    height: 1,
    backgroundColor: C.border,
    marginVertical: 10,
  },
  // Field grid (report-field-grid)
  fieldGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  fieldItem: {
    width: "33%",
    marginBottom: 10,
    paddingRight: 8,
  },
  fieldItem2col: {
    width: "50%",
    marginBottom: 10,
    paddingRight: 8,
  },
  fieldLabel: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: C.muted,
    marginBottom: 3,
  },
  fieldValue: {
    fontSize: 9,
    color: C.dark,
    fontFamily: "Helvetica",
  },
  fieldValueEmpty: {
    fontSize: 9,
    color: C.muted,
    fontFamily: "Helvetica",
    fontStyle: "italic",
  },
  // Tags row
  tagsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
  },
  tagChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 10,
    marginRight: 6,
    marginBottom: 4,
    backgroundColor: "#F7F7F7",
  },
  tagText: {
    fontSize: 8,
    color: C.dark,
    fontFamily: "Helvetica",
  },
  // MTT table
  mttTable: {
    borderWidth: 1,
    borderColor: C.border,
    marginTop: 10,
  },
  mttHeaderRow: {
    flexDirection: "row",
    backgroundColor: C.blue,
  },
  mttDataRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: C.border,
  },
  mttDataRowAlt: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: C.border,
    backgroundColor: C.rowAlt,
  },
  mttCellHeader: {
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderRightWidth: 1,
    borderRightColor: "rgba(255,255,255,0.15)",
  },
  mttCellHeaderLast: {
    paddingVertical: 7,
    paddingHorizontal: 10,
  },
  mttCellHeaderText: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
    letterSpacing: 0.5,
  },
  mttCell: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRightWidth: 1,
    borderRightColor: C.border,
  },
  mttCellLast: {
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  mttCellText: {
    fontSize: 8,
    color: C.dark,
    fontFamily: "Helvetica",
    lineHeight: 1.4,
  },
  mttValueText: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    color: C.dark,
  },
  mttValueEmpty: {
    fontSize: 8,
    color: C.muted,
    fontFamily: "Helvetica",
    fontStyle: "italic",
  },
  // Q&A blocks (5-Whys) — matches UI WhyRow style
  qaBlock: {
    flexDirection: "row",
    gap: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  qaBlockLast: {
    flexDirection: "row",
    gap: 12,
    paddingVertical: 10,
  },
  qaIndex: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: "#0073ab",
    letterSpacing: 0.5,
    minWidth: 40,
    flexShrink: 0,
    paddingTop: 2,
  },
  qaQuestion: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: C.dark,
    marginBottom: 4,
    lineHeight: 1.4,
  },
  qaAnswer: {
    fontSize: 9,
    color: C.muted,
    lineHeight: 1.6,
    fontFamily: "Helvetica",
  },
  // Chain header label (for multi-chain)
  chainHeader: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: C.muted,
    letterSpacing: 0.5,
    marginBottom: 6,
    marginTop: 10,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  // Outer border wrapping the why rows list
  whysList: {
    borderWidth: 1,
    borderColor: C.border,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  // Ticket tables
  ticketTable: {
    borderWidth: 1,
    borderColor: C.border,
    marginTop: 8,
  },
  ticketHeaderRow: {
    flexDirection: "row",
    backgroundColor: C.blue,
  },
  ticketDataRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: C.border,
  },
  ticketDataRowAlt: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: C.border,
    backgroundColor: C.rowAlt,
  },
  thCell: {
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRightWidth: 1,
    borderRightColor: "rgba(255,255,255,0.15)",
  },
  thCellLast: {
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  thText: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
    letterSpacing: 0.4,
  },
  tdCell: {
    paddingVertical: 7,
    paddingHorizontal: 6,
    borderRightWidth: 1,
    borderRightColor: C.border,
  },
  tdCellLast: {
    paddingVertical: 7,
    paddingHorizontal: 6,
  },
  tdText: {
    fontSize: 7.5,
    color: C.dark,
    fontFamily: "Helvetica",
    lineHeight: 1.4,
  },
  statusBadge: {
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 8,
  },
  statusBadgeText: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
  },
  // Images
  imageBlock: {
    marginTop: 10,
  },
  image: {
    maxHeight: 200,
    objectFit: "contain",
    marginBottom: 8,
  },
  // Content box (matches report-content-box)
  contentBox: {
    backgroundColor: "#FAFAFA",
    borderWidth: 1,
    borderColor: C.border,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 2,
  },
  contentBoxText: {
    fontSize: 9,
    color: C.dark,
    fontFamily: "Helvetica",
    lineHeight: 1.6,
  },
  contentBoxEmpty: {
    backgroundColor: "#FAFAFA",
    borderWidth: 1,
    borderColor: C.border,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 2,
  },
  contentBoxEmptyText: {
    fontSize: 9,
    color: C.muted,
    fontFamily: "Helvetica",
    fontStyle: "italic",
  },
});

// ── Logo (white text for navy background) ──────────────────
const MaerskLogoWhite = ({ width = 90, height = 22 }) => (
  <Svg width={width} height={height} viewBox="0 0 135 31">
    <G fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
      <G>
        <Path fill="#FFFFFF" d="M41.179633 21.6666055L41.179633 8.46880734 44.6915596 8.46880734 49.1414679 18.3313761 53.5253211 8.46880734 56.8897248 8.46880734 56.8897248 21.6666055 53.9937615 21.6666055 53.9937615 13.9888073 50.6524771 21.6666055 47.5655046 21.6666055 44.1812844 14.0306422 44.1812844 21.6666055 41.179633 21.6666055" />
        <Path fill="#FFFFFF" d="M68.544 16.557l-2.423-5.595-2.368 5.595h4.791zm-6.957 5.11h-3.405l5.854-13.198h4.193l5.896 13.198h-3.362l-1.16-2.63h-6.891l-1.125 2.63z" />
        <Path fill="#FFFFFF" d="M75.4825688 21.6666055L75.4825688 8.46880734 88.0836697 8.46880734 88.0836697 11.1077064 78.526055 11.1077064 78.526055 13.7289908 86.7207339 13.7289908 86.7207339 16.3045872 78.526055 16.3045872 78.526055 19.0266055 88.106789 19.0266055 88.106789 21.6666055 75.4825688 21.6666055" />
        <Path fill="#FFFFFF" d="M93.644 14.529h6.621v-3.421h-6.62v3.42zm-3.086 7.138V8.469h10.545c1.408 0 2.226.815 2.226 2.213v4.434c0 1.02-.903 1.942-2.654 1.942-.1.005-.2 0-.305 0l3.07 4.609h-3.388l-2.91-4.513h-3.498v4.513h-3.086z" />
        <Path fill="#FFFFFF" d="M108.882 21.674h-1.045c-1.364 0-2.02-.788-2.02-1.936v-1.724h3.065v1.021h6.513v-2.701h-7.471c-1.193 0-2.052-.802-2.052-1.915V10.5c0-1.186.935-2.024 2.073-2.024h8.54c1.155 0 1.954.862 1.954 2.107v1.575h-3.044v-1.043h-6.513v2.6h7.45c1.1 0 2.107.727 2.107 2.128v3.767c0 1.283-.712 2.063-2.043 2.063h-7.514" />
        <Path fill="#FFFFFF" d="M121.380367 21.6743119L121.380367 8.47541284 124.509174 8.47541284 124.509174 13.7543119 130.468991 8.47541284 134.705321 8.47541284 127.595596 14.606422 134.961284 21.6743119 130.575229 21.6743119 124.509174 15.6710092 124.509174 21.6743119 121.380367 21.6743119" />
        <Path fill="#42B0D5" d="M28.554 26.63a3.415 3.415 0 01-3.415 3.416H3.581a3.416 3.416 0 01-3.416-3.415V3.503A3.416 3.416 0 013.581.087H25.14a3.416 3.416 0 013.415 3.416v23.128" />
        <Path fill="#FFFFFE" d="M18.8855046 14.2893578L24.4875229 7.26385321 24.4699083 7.2412844 16.3737615 11.1401835 14.373945 2.37908257 14.3458716 2.37908257 12.346055 11.1401835 4.24990826 7.2412844 4.23229358 7.26385321 9.83486239 14.2893578 1.73816514 18.1882569 1.7453211 18.2157798 10.7310092 18.2157798 8.73119266 26.9774312 8.75651376 26.9889908 14.3601835 19.9634862 19.9627523 26.9889908 19.9880734 26.9763303 17.9893578 18.2157798 26.9750459 18.2157798 26.9816514 18.1882569 18.8855046 14.2893578" />
      </G>
    </G>
  </Svg>
);

// ── Reusable building blocks ────────────────────────────────

const SectionCard = ({ title, children }) => (
  <View style={S.sectionCard}>
    <View style={S.sectionHeader}>
      <Text style={S.sectionHeaderText}>{title}</Text>
    </View>
    <View style={S.sectionBody}>
      {children}
    </View>
  </View>
);

const MetaField = ({ label, value, isLink, snowLink }) => (
  <View style={S.metaField}>
    <Text style={S.metaLabel}>{label}</Text>
    {isLink && snowLink && value ? (
      <Link src={`${snowLink}number=${value}`} style={{ textDecoration: "none" }}>
        <Text style={S.metaLink}>{value}</Text>
      </Link>
    ) : (
      <Text style={S.metaValue}>{value || "NA"}</Text>
    )}
  </View>
);

const FieldItem = ({ label, value, twoCol }) => (
  <View style={twoCol ? S.fieldItem2col : S.fieldItem}>
    <Text style={S.fieldLabel}>{label}</Text>
    <Text style={value ? S.fieldValue : S.fieldValueEmpty}>
      {value || "Not set"}
    </Text>
  </View>
);

const ContentBox = ({ text }) =>
  text ? (
    <View style={S.contentBox}>
      <Text style={S.contentBoxText}>{stripHtml(text)}</Text>
    </View>
  ) : (
    <View style={S.contentBoxEmpty}>
      <Text style={S.contentBoxEmptyText}>NA</Text>
    </View>
  );

const ImageBlock = ({ images }) =>
  images?.length > 0 ? (
    <View style={S.imageBlock}>
      {images.map((img, i) => (
        <Image key={i} style={S.image} src={img.preview} />
      ))}
    </View>
  ) : null;

// MTT metrics table (matches DetectionAndResponseSection)
const MTT_METRICS = [
  { key: "mttd",      name: "Mean Time To Detect",  description: "Time between the problem first occurring, and when it was detected (by human or automation)" },
  { key: "mttrespond",name: "Mean Time To Respond", description: "Time between the issue being detected and an engineer beginning investigations." },
  { key: "mttrepair", name: "Mean Time To Repair",  description: "Time between the engineer investigation starting and service being restored." },
  { key: "mttrecover",name: "Mean Time To Recover", description: "Time between the problem first occurring and service being restored." },
];

const MttTable = ({ data }) => (
  <View style={S.mttTable}>
    <View style={S.mttHeaderRow}>
      <View style={[S.mttCellHeader, { flex: 2 }]}>
        <Text style={S.mttCellHeaderText}>METRIC</Text>
      </View>
      <View style={[S.mttCellHeader, { flex: 4 }]}>
        <Text style={S.mttCellHeaderText}>DESCRIPTION</Text>
      </View>
      <View style={[S.mttCellHeaderLast, { flex: 1.5 }]}>
        <Text style={S.mttCellHeaderText}>VALUE (MINUTES)</Text>
      </View>
    </View>
    {MTT_METRICS.map((m, i) => {
      const val = data?.[m.key];
      return (
        <View key={m.key} style={i % 2 === 1 ? S.mttDataRowAlt : S.mttDataRow}>
          <View style={[S.mttCell, { flex: 2 }]}>
            <Text style={[S.mttCellText, { fontFamily: "Helvetica-Bold", color: C.navy }]}>{m.name}</Text>
          </View>
          <View style={[S.mttCell, { flex: 4 }]}>
            <Text style={S.mttCellText}>{m.description}</Text>
          </View>
          <View style={[S.mttCellLast, { flex: 1.5 }]}>
            {val != null ? (
              <Text style={S.mttValueText}>{String(val)}</Text>
            ) : (
              <Text style={S.mttValueEmpty}>Please fill on edit page</Text>
            )}
          </View>
        </View>
      );
    })}
  </View>
);

// Jira tickets table (matches JiraSection web view)
const JiraTable = ({ data }) => {
  if (!data?.length) {
    return (
      <View style={S.contentBoxEmpty}>
        <Text style={S.contentBoxEmptyText}>NA</Text>
      </View>
    );
  }
  return (
    <View style={S.ticketTable}>
      <View style={S.ticketHeaderRow}>
        {[
          { label: "JIRA ID",     flex: 1.2 },
          { label: "SUMMARY",     flex: 2.8 },
          { label: "ASSIGNED TO", flex: 1.4 },
          { label: "PORTFOLIO",   flex: 1.4 },
          { label: "FIX TYPE",    flex: 1.4 },
          { label: "STATUS",      flex: 1 },
          { label: "DUE DATE",    flex: 1, last: true },
        ].map((col) => (
          <View key={col.label} style={[col.last ? S.thCellLast : S.thCell, { flex: col.flex }]}>
            <Text style={S.thText}>{col.label}</Text>
          </View>
        ))}
      </View>
      {data.map((ticket, i) => {
        const sc = getStatusColors(ticket.status, ticket.duedate);
        return (
          <View key={i} style={i % 2 === 1 ? S.ticketDataRowAlt : S.ticketDataRow}>
            <View style={[S.tdCell, { flex: 1.2 }]}>
              <Text style={[S.tdText, { color: C.blue, fontFamily: "Helvetica-Bold" }]}>
                {ticket.jiraid?.[0] || "—"}
              </Text>
            </View>
            <View style={[S.tdCell, { flex: 2.8 }]}>
              <Text style={S.tdText}>{ticket.description || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.4 }]}>
              <Text style={S.tdText}>{ticket.assignee || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.4 }]}>
              <Text style={S.tdText}>{ticket.platformPortfolio || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.4 }]}>
              <Text style={S.tdText}>{ticket.horizontalVerticalFix || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1 }]}>
              {ticket.status ? (
                <View style={[S.statusBadge, { backgroundColor: sc.bg }]}>
                  <Text style={[S.statusBadgeText, { color: sc.text }]}>{ticket.status}</Text>
                </View>
              ) : (
                <Text style={S.tdText}>—</Text>
              )}
            </View>
            <View style={[S.tdCellLast, { flex: 1 }]}>
              <Text style={S.tdText}>{ticket.duedate || "—"}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

// PTask table (matches PTaskSection web view)
const PTaskTable = ({ data }) => {
  if (!data?.length) {
    return (
      <View style={S.contentBoxEmpty}>
        <Text style={S.contentBoxEmptyText}>NA</Text>
      </View>
    );
  }
  return (
    <View style={S.ticketTable}>
      <View style={S.ticketHeaderRow}>
        {[
          { label: "PTASK ID",         flex: 1.2 },
          { label: "SUMMARY",          flex: 2.4 },
          { label: "ASSIGNED TO",      flex: 1.3 },
          { label: "ASSIGNMENT GROUP", flex: 1.3 },
          { label: "PORTFOLIO",        flex: 1.2 },
          { label: "FIX TYPE",         flex: 1.2 },
          { label: "STATUS",           flex: 0.9 },
          { label: "DUE DATE",         flex: 0.9, last: true },
        ].map((col) => (
          <View key={col.label} style={[col.last ? S.thCellLast : S.thCell, { flex: col.flex }]}>
            <Text style={S.thText}>{col.label}</Text>
          </View>
        ))}
      </View>
      {data.map((ticket, i) => {
        const sc = getStatusColors(ticket.status, ticket.eta);
        const ownersText = typeof ticket.owners === "string"
          ? ticket.owners
          : Array.isArray(ticket.owners) ? ticket.owners.join(", ") : (ticket.assignee || "—");
        return (
          <View key={i} style={i % 2 === 1 ? S.ticketDataRowAlt : S.ticketDataRow}>
            <View style={[S.tdCell, { flex: 1.2 }]}>
              <Text style={[S.tdText, { color: C.blue, fontFamily: "Helvetica-Bold" }]}>
                {ticket.ptaskid?.[0] || "—"}
              </Text>
            </View>
            <View style={[S.tdCell, { flex: 2.4 }]}>
              <Text style={S.tdText}>{ticket.description || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.3 }]}>
              <Text style={S.tdText}>{ownersText}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.3 }]}>
              <Text style={S.tdText}>{ticket.assignment_group || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.2 }]}>
              <Text style={S.tdText}>{ticket.platformPortfolio || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 1.2 }]}>
              <Text style={S.tdText}>{ticket.horizontalVerticalFix || "—"}</Text>
            </View>
            <View style={[S.tdCell, { flex: 0.9 }]}>
              {ticket.status ? (
                <View style={[S.statusBadge, { backgroundColor: sc.bg }]}>
                  <Text style={[S.statusBadgeText, { color: sc.text }]}>{ticket.status}</Text>
                </View>
              ) : (
                <Text style={S.tdText}>—</Text>
              )}
            </View>
            <View style={[S.tdCellLast, { flex: 0.9 }]}>
              <Text style={S.tdText}>{ticket.eta || "—"}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

// ── Main component ──────────────────────────────────────────
function ReportDoc({
  graphData,
  postmortemData,
  styles: _propStyles,
  uploadedDetectionAndResponseImages,
  uploadedImpactImages,
  uploadedPrecedingEventsImages,
  uploadedRootCauseImages,
  uploadedSummaryImages,
  rootCauseIdentificationUploadedImages,
  jiraUploadedImages,
  ptaskUploadedImages,
  chainedFiveWhysData = [],
  incidentMetadata,
  mttdMinutes,
  mttrMinutes,
}) {
  const snowLink = process.env.REACT_APP_SNOW_LINK;
  const priority = postmortemData?.priority;
  const rcData = postmortemData?.rootCause;
  const jiraList = postmortemData?.preventiveCorrectiveMeasures?.[0]?.jira || [];
  const ptaskList = postmortemData?.preventiveCorrectiveMeasures?.[0]?.ptask || [];
  const questions = (postmortemData?.rootCauseIdentificationRequest?.questionarie || [])
    .filter((q) => q?.question && q?.answer);
  const hasClassicQuestions = questions && questions.length > 0;
  const hasChainedData = chainedFiveWhysData && chainedFiveWhysData.length > 0;

  const status = postmortemData?.postMortemIncomplete ? "INCOMPLETE" : postmortemData?.status;
  const capturedByAlert = postmortemData?.channelName?.toLowerCase().includes("alert");

  const formatMinutes = (mins) => {
    if (mins === undefined || mins === null || mins === "") return "—";
    const n = parseFloat(mins);
    if (isNaN(n)) return "—";
    if (n < 60) return `${Math.round(n)} min`;
    const h = Math.floor(n / 60);
    const m = Math.round(n % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // Prefer insights-API values (same source as Report UI); fall back to detectionResponse
  const mttd = mttdMinutes != null ? mttdMinutes : postmortemData?.detectionResponse?.mttd;
  const mttr = mttrMinutes != null ? mttrMinutes : postmortemData?.detectionResponse?.mttrecover;
  const kpiColor = (isGood) => isGood ? C.success : C.error;
  const mttdGood = !isNaN(parseFloat(mttd)) && parseFloat(mttd) <= 15;
  const mttrGood = !isNaN(parseFloat(mttr)) && parseFloat(mttr) <= 30;

  const vendorText =
    rcData?.causedByVendor && rcData.causedByVendor !== "" && rcData.causedByVendor !== "No"
      ? rcData.causedByVendor === "Yes" && (rcData?.vendorOptions || rcData?.vendorDetails)
        ? `Yes (${rcData.vendorOptions || rcData.vendorDetails})`
        : rcData.causedByVendor
      : "No";

  const causeByChange = rcData?.causedByChangeNumber
    ? `Yes — ${rcData.causedByChangeNumber}`
    : "No";

  const headerTitle = postmortemData?.short_description || postmortemData?.application || postmortemData?.product || postmortemData?.customID || "—";
  const footerText = postmortemData?.short_description || postmortemData?.incidentID || "";

  return (
    <Document title="Postmortem Report">
      <Page size="A4" style={S.page}>
        {/* Fixed Header */}
        <View style={S.fixedHeader} fixed>
          <MaerskLogoWhite width={90} height={22} />
          <Text style={S.fixedHeaderRight}>POSTMORTEM REPORT  ·  CONFIDENTIAL</Text>
        </View>

        {/* Fixed Footer */}
        <View style={S.fixedFooter} fixed>
          <Text style={S.fixedFooterText}>{footerText}</Text>
          <Text
            style={S.fixedFooterText}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>

        {/* ── 1. Header Info Card ──────────────────────── */}
        <SectionCard title={headerTitle}>
          {/* Row 1 — Incident + KPIs (4 equal columns, mirrors web view) */}
          <View style={{ flexDirection: "row", borderBottomWidth: 1, borderBottomColor: C.border, paddingBottom: 10, marginBottom: 10 }}>
            {/* Col 1: Incident number + IRC link */}
            <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
              <Text style={S.metaLabel}>INCIDENT</Text>
              {postmortemData?.incidentID ? (
                snowLink ? (
                  <Link src={`${snowLink}number=${postmortemData.incidentID}`} style={{ textDecoration: "none" }}>
                    <Text style={[S.metaLink, { marginTop: 3 }]}>{postmortemData.incidentID}</Text>
                  </Link>
                ) : (
                  <Text style={[S.metaValue, { marginTop: 3 }]}>{postmortemData.incidentID}</Text>
                )
              ) : (
                <Text style={[S.metaValue, { marginTop: 3 }]}>NA</Text>
              )}
              {incidentMetadata?.ircLink ? (
                <Link src={incidentMetadata.ircLink} style={{ textDecoration: "none", marginTop: 4 }}>
                  <Text style={S.metaLink}>Open Hedwig IRC</Text>
                </Link>
              ) : null}
            </View>
            {/* Col 2: MTTD */}
            <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10, alignItems: "center", justifyContent: "center" }}>
              <Text style={[S.metaLabel, { textAlign: "center" }]}>MEAN TIME TO DETECT</Text>
              <Text style={[S.metaValue, { color: kpiColor(mttdGood), fontFamily: "Helvetica-Bold", fontSize: 11, textAlign: "center", marginTop: 3 }]}>{formatMinutes(mttd)}</Text>
            </View>
            {/* Col 3: MTTR */}
            <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10, alignItems: "center", justifyContent: "center" }}>
              <Text style={[S.metaLabel, { textAlign: "center" }]}>MEAN TIME TO RECOVER</Text>
              <Text style={[S.metaValue, { color: kpiColor(mttrGood), fontFamily: "Helvetica-Bold", fontSize: 11, textAlign: "center", marginTop: 3 }]}>{formatMinutes(mttr)}</Text>
            </View>
            {/* Col 4: Captured by Alert */}
            <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
              <Text style={[S.metaLabel, { textAlign: "center" }]}>CAPTURED BY ALERT</Text>
              <Text style={[S.metaValue, { color: kpiColor(capturedByAlert), fontFamily: "Helvetica-Bold", fontSize: 11, textAlign: "center", marginTop: 3 }]}>{capturedByAlert ? "Yes" : "No"}</Text>
            </View>
          </View>

          {/* Row 2 — Dates + Application (4 equal columns, mirrors web view) */}
          <View style={{ flexDirection: "row", borderBottomWidth: 1, borderBottomColor: C.border, paddingBottom: 10, marginBottom: 10 }}>
            <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
              <Text style={S.metaLabel}>CREATED ON</Text>
              <Text style={[S.metaValue, { marginTop: 3 }]}>
                {formatUtcDate(incidentMetadata?.incidentCreatedOn)}
              </Text>
            </View>
            <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
              <Text style={S.metaLabel}>AFFECTED LOCATIONS</Text>
              {incidentMetadata?.affectedLocations && incidentMetadata.affectedLocations !== "NA" ? (
                <View style={{ marginTop: 3, flexDirection: "row", flexWrap: "wrap", gap: 3 }}>
                  {incidentMetadata.affectedLocations.split(",").slice(0, 5).map((loc, idx) => (
                    <Text key={idx} style={[S.metaValue, { fontSize: 9, backgroundColor: C.bgLight, padding: "2 6", borderRadius: 8 }]}>
                      {loc.trim()}
                    </Text>
                  ))}
                  {incidentMetadata.affectedLocations.split(",").length > 5 && (
                    <Text style={[S.metaValue, { fontSize: 9, fontStyle: "italic" }]}>
                      +{incidentMetadata.affectedLocations.split(",").length - 5} more
                    </Text>
                  )}
                </View>
              ) : (
                <Text style={[S.metaValue, { marginTop: 3 }]}>NA</Text>
              )}
              {incidentMetadata?.affectedClient && incidentMetadata.affectedClient !== "NA" && (
                <>
                  <Text style={[S.metaLabel, { marginTop: 6 }]}>AFFECTED CUSTOMER</Text>
                  <Text style={[S.metaValue, { marginTop: 3 }]}>
                    {incidentMetadata.affectedClient}
                  </Text>
                </>
              )}
            </View>
            <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
              <Text style={S.metaLabel}>AFFECTED APPLICATION</Text>
              <Text style={[S.metaValue, { marginTop: 3 }]}>{postmortemData?.cmdb_ci || "NA"}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={S.metaLabel}>CAUSED BY APPLICATION</Text>
              <Text style={[S.metaValue, { marginTop: 3 }]}>{postmortemData?.application || "NA"}</Text>
              
              {/* APMT Location Data */}
              {(postmortemData?.apmtRegionNamingConvention || postmortemData?.apmtLocationAlias) && (
                <>
                  {postmortemData?.apmtRegionNamingConvention && (
                    <>
                      <Text style={[S.metaLabel, { marginTop: 6 }]}>APMT REGION</Text>
                      <Text style={[S.metaValue, { marginTop: 3, fontSize: 9, backgroundColor: C.bgLight, padding: "2 6", borderRadius: 8 }]}>
                        {postmortemData.apmtRegionNamingConvention}
                      </Text>
                    </>
                  )}
                  {postmortemData?.apmtLocationAlias && (
                    <>
                      <Text style={[S.metaLabel, { marginTop: 6 }]}>APMT LOCATION</Text>
                      <Text style={[S.metaValue, { marginTop: 3, fontSize: 9, backgroundColor: C.bgLight, padding: "2 6", borderRadius: 8 }]}>
                        {postmortemData.apmtLocationAlias}
                      </Text>
                    </>
                  )}
                </>
              )}
            </View>
          </View>

          {/* Row 3 — Owner | Status | Root Cause Code | Flags (4 equal columns, mirrors web view) */}
          {(() => {
            const isVendor = rcData?.causedByVendor && rcData.causedByVendor !== "No" && rcData.causedByVendor !== "";
            const vendorName = rcData?.vendorOptions || rcData?.vendorDetails;
            const isChange = rcData?.impactedByChange === true || rcData?.impactedByChange === "true" || rcData?.impactedByChange === "Yes";
            const changeNum = rcData?.causedByChangeNumber;
            const recurringIncidentId = joinReferenceIds(getPastReferencesList(postmortemData?.recurrence));
            const isRecurring = !!(
              (postmortemData?.recurrence?.recurringIssue && postmortemData.recurrence.recurringIssue !== "No" && postmortemData.recurrence.recurringIssue !== "false" && postmortemData.recurrence.recurringIssue !== false) ||
              (rcData?.recurringIssue && rcData.recurringIssue !== "No" && rcData.recurringIssue !== "false" && rcData.recurringIssue !== false)
            );
            const ageing = incidentMetadata?.ageing;
            const isOld = ageing != null && ageing >= 5;
            const daysOver = ageing != null ? ageing - 5 : 0;
            const statusColor = isOld ? C.error : C.success;

            let cta, statusSub;
            const elapsed = ageing != null ? `Leadership Review Age: ${ageing}d` : "";
            switch (status?.toUpperCase()) {
              case "DRAFT": cta = "Pending Leadership Review"; statusSub = `RCA in progress · ${elapsed}`; break;
              case "LEADERSHIP_REVIEW": cta = "In Leadership Review"; statusSub = `Awaiting ED or TLT approval · ${elapsed}`; break;
              case "DONE": {
                const t1 = incidentMetadata?.postmortemCreatedTime;
                const t2 = incidentMetadata?.postmortemCompletedTime;
                let closedIn = ageing != null ? `${ageing}d` : null;
                if (t1 && t2) {
                  const diffMs = new Date(t2) - new Date(t1);
                  const d = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const h = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                  closedIn = d > 0 ? `${d}d ${h}h` : `${h}h`;
                }
                cta = "Postmortem Closed";
                statusSub = closedIn
                  ? (isOld ? `Closed in ${closedIn} · Leadership Review Age: ${ageing}d · SLA exceeded by ${daysOver}d` : `Closed in ${closedIn} · Leadership Review Age: ${ageing}d · Within SLA`)
                  : (isOld ? `Leadership Review Age: ${ageing}d · SLA exceeded by ${daysOver}d` : "Within SLA");
                break;
              }
              default: cta = status || "—"; statusSub = elapsed;
            }

            return (
              <View style={{ flexDirection: "row" }}>
                {/* Col 1: Postmortem Owner */}
                <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
                  <Text style={S.metaLabel}>POSTMORTEM OWNER</Text>
                  <Text style={[S.metaValue, { marginTop: 3 }]}>
                    {postmortemData?.assigned_to ? formatEmailToName(postmortemData.assigned_to) : "NA"}
                  </Text>
                </View>
                {/* Col 2: Postmortem Status */}
                <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
                  <Text style={[S.metaLabel, { marginBottom: 6 }]}>POSTMORTEM STATUS</Text>
                  <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
                    <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: statusColor, marginRight: 5 }} />
                    <Text style={{ fontSize: 9, fontFamily: "Helvetica-Bold", color: statusColor }}>{cta}</Text>
                  </View>
                  <Text style={{ fontSize: 8, color: C.dark, fontFamily: "Helvetica" }}>{statusSub}</Text>
                </View>
                {/* Col 3: Root Cause Code + Sub Code when flags are present */}
                <View style={{ flex: 1, paddingRight: 10, borderRightWidth: 1, borderRightColor: C.border, marginRight: 10 }}>
                  <Text style={S.metaLabel}>ROOT CAUSE CODE</Text>
                  <Text style={[S.metaValue, { marginTop: 3 }]}>{rcData?.rootCauseCode || "NA"}</Text>
                  {(isChange || isVendor || isRecurring) && (
                    <View style={{ marginTop: 10 }}>
                      <Text style={S.metaLabel}>ROOT CAUSE SUB CODE</Text>
                      <Text style={[S.metaValue, { marginTop: 3 }]}>{rcData?.rootCauseSubCode || "NA"}</Text>
                    </View>
                  )}
                </View>
                {/* Col 4: Flags when present, else Sub Code */}
                <View style={{ flex: 1 }}>
                  {(isChange || isVendor || isRecurring) ? (
                    <View style={{ gap: 10 }}>
                      {isChange && (
                        <View>
                          <Text style={S.metaLabel}>CAUSED BY CHANGE</Text>
                          <Text style={[S.metaValue, { marginTop: 3, color: C.error, fontFamily: "Helvetica-Bold" }]}>Yes{changeNum ? ` — ${changeNum}` : ""}</Text>
                        </View>
                      )}
                      {isVendor && (
                        <View style={{ marginTop: isChange ? 8 : 0 }}>
                          <Text style={S.metaLabel}>CAUSED BY VENDOR</Text>
                          <Text style={[S.metaValue, { marginTop: 3, color: C.error, fontFamily: "Helvetica-Bold" }]}>Yes{vendorName ? ` — ${vendorName}` : ""}</Text>
                        </View>
                      )}
                      {isRecurring && (
                        <View style={{ marginTop: (isChange || isVendor) ? 8 : 0 }}>
                          <Text style={S.metaLabel}>RECURRENCE</Text>
                          <Text style={[S.metaValue, { marginTop: 3, color: C.error, fontFamily: "Helvetica-Bold" }]}>Yes{recurringIncidentId ? ` — ${recurringIncidentId}` : ""}</Text>
                        </View>
                      )}
                    </View>
                  ) : (
                    <View>
                      <Text style={S.metaLabel}>ROOT CAUSE SUB CODE</Text>
                      <Text style={[S.metaValue, { marginTop: 3 }]}>{rcData?.rootCauseSubCode || "NA"}</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })()}
        </SectionCard>

        {/* ── 2. Incident Summary ──────────────────────── */}
        <SectionCard title="Incident Summary">
          <ContentBox text={postmortemData?.incident_summary?.summary} />
          <ImageBlock images={uploadedSummaryImages} />
        </SectionCard>

        {/* ── 3. Preceding Events (legacy — only when populated) ── */}
        {!!stripHtml(postmortemData?.precedingEvents?.description) && (
          <SectionCard title="Preceding Events">
            <ContentBox text={postmortemData.precedingEvents.description} />
            <ImageBlock images={uploadedPrecedingEventsImages} />
          </SectionCard>
        )}

        {/* ── 4. Business Impact (legacy — only when populated) ── */}
        {!!stripHtml(postmortemData?.businessImpact?.description) && (
          <SectionCard title="Business Impact">
            <ContentBox text={postmortemData.businessImpact.description} />
            <ImageBlock images={uploadedImpactImages} />
          </SectionCard>
        )}

        {/* ── 5. Recovery (legacy — only when populated) ── */}
        {!!stripHtml(postmortemData?.recovery?.recovery) && (
          <SectionCard title="Recovery">
            <ContentBox text={postmortemData.recovery.recovery} />
          </SectionCard>
        )}

        {/* ── 6. Root Cause (identification + 5 whys + cause text + code) ── */}
        <SectionCard title="Root Cause Identification">
          <ContentBox text={postmortemData?.rootCauseIdentificationRequest?.description} />
          <Text style={S.subsectionTitle}>5 WHYS ANALYSIS</Text>
          {hasClassicQuestions ? (
            /* Classic 5 Whys — single bordered card, WhyRow style matching UI */
            <View style={S.whysList}>
              {questions.map((q, i) => (
                <View key={i} style={i === questions.length - 1 ? S.qaBlockLast : S.qaBlock} wrap={false}>
                  <Text style={S.qaIndex}>WHY {i + 1}</Text>
                  <View style={{ flex: 1 }}>
                    {q.question ? <Text style={S.qaQuestion}>{q.question}</Text> : null}
                    <Text style={S.qaAnswer}>{stripHtml(q.answer) || "No answer provided"}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : hasChainedData ? (
            /* Chained 5 Whys — each chain as a labelled group, same WhyRow rows */
            <View style={{ marginTop: 4 }}>
              {chainedFiveWhysData.map((chain, chainIndex) => {
                const letter = chain.chainLetter || String.fromCharCode(65 + chainIndex);
                const label = chain.chainLabel ? `${letter} — ${chain.chainLabel}` : letter;
                const desc = chain.chainDescription && stripHtml(chain.chainDescription).trim();
                const whys = chain.whys || [];
                return (
                  <View key={chain.chainId || chainIndex} style={{ marginBottom: chainIndex < chainedFiveWhysData.length - 1 ? 14 : 0 }}>
                    {/* Chain label header */}
                    <Text style={S.chainHeader}>{label.toUpperCase()}</Text>
                    {desc ? (
                      <Text style={{ fontSize: 8, color: C.muted, lineHeight: 1.5, marginBottom: 6 }}>{desc}</Text>
                    ) : null}
                    {/* Why rows in bordered card */}
                    {whys.length > 0 ? (
                      <View style={S.whysList}>
                        {whys.map((why, wi) => (
                          <View key={wi} style={wi === whys.length - 1 ? S.qaBlockLast : S.qaBlock} wrap={false}>
                            <Text style={S.qaIndex}>WHY {why.whyNumber || wi + 1}</Text>
                            <View style={{ flex: 1 }}>
                              {why.question ? <Text style={S.qaQuestion}>{why.question}</Text> : null}
                              <Text style={S.qaAnswer}>{stripHtml(why.answer) || "No answer provided"}</Text>
                            </View>
                          </View>
                        ))}
                      </View>
                    ) : (
                      <View style={S.contentBoxEmpty}>
                        <Text style={S.contentBoxEmptyText}>No whys recorded</Text>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          ) : (
            <View style={S.contentBoxEmpty}>
              <Text style={S.contentBoxEmptyText}>NA</Text>
            </View>
          )}
          <ImageBlock images={rootCauseIdentificationUploadedImages} />

          {/* Root Cause description text (legacy — only when populated) */}
          {!!stripHtml(postmortemData?.rootCause?.cause) && (
            <>
              <Text style={S.subsectionTitle}>ROOT CAUSE</Text>
              <ContentBox text={postmortemData.rootCause.cause} />
            </>
          )}

          {/* Root Cause Code */}
          <Text style={S.subsectionTitle}>ROOT CAUSE CODE</Text>

          {/* Code + Sub Code + Sub Category */}
          <View style={S.fieldGrid}>
            <FieldItem label="Root Cause Code" value={rcData?.rootCauseCode} />
            <FieldItem label="Root Cause Sub Code" value={rcData?.rootCauseSubCode} />
            {(rcData?.rootCauseSubCategory || rcData?.subCategory) && (
              <FieldItem label="Sub Category" value={rcData?.rootCauseSubCategory || rcData?.subCategory} />
            )}
          </View>

          {/* Caused By Vendor + Caused By Change */}
          <View style={[S.fieldGrid, { borderTopWidth: 1, borderTopColor: C.border, marginTop: 8, paddingTop: 8 }]}>
            <FieldItem label="Caused By Vendor" value={vendorText} twoCol />
            <FieldItem label="Caused By Change" value={causeByChange} twoCol />
          </View>

          {/* Recurrence */}
          {(rcData?.recurringIssue || rcData?.incidentId || rcData?.problemId) && (
            <>
              <View style={S.divider} />
              <Text style={[S.fieldLabel, { marginBottom: 6 }]}>Recurrence</Text>
              <View style={S.fieldGrid}>
                <FieldItem label="Incident ID" value={rcData?.incidentId} twoCol />
                <FieldItem label="Problem ID"  value={rcData?.problemId}  twoCol />
              </View>
            </>
          )}

          <ImageBlock images={uploadedRootCauseImages} />
        </SectionCard>

        {/* ── 8. MVC Vulnerability (APMT only) ────────── */}
        {postmortemData?.portfolio === "APMT, IOT and Automation" && (
          <SectionCard title="MVC Vulnerability Related">
            <ContentBox
              text={
                postmortemData?.mvcVulnerabilityRelated?.length > 0
                  ? postmortemData.mvcVulnerabilityRelated.join(", ")
                  : undefined
              }
            />
          </SectionCard>
        )}

        {/* ── 7. Preventive & Corrective Measures ────── */}
        <SectionCard title="Preventive & Corrective Measures">
          <Text style={S.subsectionTitle} render={() => "JIRA TICKETS"} />
          <JiraTable data={jiraList} />
          <ImageBlock images={jiraUploadedImages} />

          <Text style={[S.subsectionTitle, { marginTop: 14 }]} render={() => "PTASK"} />
          <PTaskTable data={ptaskList} />
          <ImageBlock images={ptaskUploadedImages} />
        </SectionCard>
      </Page>
    </Document>
  );
}

export default ReportDoc;
